/**
 * useReplyTargets — the composer's pending "replying to" per thread
 * (reply to a specific message, Miles 2026-09-15).
 *
 * One map, keyed by thread id, so each conversation keeps its own pending
 * reply across a thread switch (the same per-thread discipline the drafts
 * keep). Two views of it: React STATE for the strip above the composer and
 * the row button's pressed look, and a REF MIRROR for the send paths, which
 * read the target at the instant a message goes out without re-rendering or
 * waiting on a state flush. Starting a reply replaces the thread's previous
 * target (one reply at a time — a quote answers one message); sending,
 * cancelling and archiving clear it.
 */
import { useCallback, useMemo, useRef, useState } from 'react';
import type { MessageReplyRef } from '../../types/messageReply';

export type ReplyTargetsByThread = Readonly<Record<string, MessageReplyRef>>;

export interface ReplyTargetsApi {
  /** The pending reply target for a thread, or null. */
  forThread: (threadId: string | null | undefined) => MessageReplyRef | null;
  /** Start (or replace) the thread's pending reply. */
  start: (threadId: string, ref: MessageReplyRef) => void;
  /** Drop the thread's pending reply (cancel, send, archive). */
  clear: (threadId: string | null | undefined) => void;
  /** The send paths read here — always current, never a stale closure. */
  ref: { readonly current: ReplyTargetsByThread };
  /** The whole map, for effects that persist a thread's target with its draft. */
  byThread: ReplyTargetsByThread;
}

export function useReplyTargets(): ReplyTargetsApi {
  const [byThread, setByThread] = useState<ReplyTargetsByThread>({});
  const mirror = useRef<ReplyTargetsByThread>({});

  const publish = useCallback((next: ReplyTargetsByThread) => {
    mirror.current = next;
    setByThread(next);
  }, []);

  const start = useCallback((threadId: string, ref: MessageReplyRef) => {
    const id = (threadId || '').trim();
    if (!id || !ref || !ref.messageId) return;
    publish({ ...mirror.current, [id]: ref });
  }, [publish]);

  const clear = useCallback((threadId: string | null | undefined) => {
    const id = (threadId || '').trim();
    if (!id || !(id in mirror.current)) return;
    const next = { ...mirror.current };
    delete next[id];
    publish(next);
  }, [publish]);

  const forThread = useCallback((threadId: string | null | undefined): MessageReplyRef | null => {
    const id = (threadId || '').trim();
    return id ? byThread[id] ?? null : null;
  }, [byThread]);

  return useMemo(() => ({ forThread, start, clear, ref: mirror, byThread }), [forThread, start, clear, byThread]);
}
